import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import './App.css';
import AdminPanel from './AdminPanel';
import AdminPage from './AdminPage';
import HomePage from './HomePage';
import useSpeechRecognition from './useSpeechRecognition';

// 主游戏页面组件
function GamePage() {
  const [sortedQuestions, setSortedQuestions] = useState([]);
  // 题目相关
  const [question, setQuestion] = useState(null);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const { id } = useParams();
  const questionId = parseInt(id || 1, 10);
  const navigate = useNavigate();

  // 聊天相关
  const [messages, setMessages] = useState([]);
  const [feedbackStatus, setFeedbackStatus] = useState({});
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(0); // 初始化为0，后续动态设置
  const [gameOver, setGameOver] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [selectedBubble, setSelectedBubble] = useState(-1);

  const messagesEndRef = useRef(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }, [messages]);

  // 在 useEffect 中监听 messages 变化
  useEffect(() => {
    setSelectedBubble(-1); // 新增：重置选中气泡状态
  }, [messages]);

  // 提示相关
  const [hintIndex, setHintIndex] = useState(0);
  const [currentHint, setCurrentHint] = useState('');
  const [hintHistory, setHintHistory] = useState([]);

  // 点赞相关
  const [likes, setLikes] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [notification, setNotification] = useState(null);

  // 相似度
  const [similarityLoading, setSimilarityLoading] = useState(false);

  const { isListening, startListening, stopListening } = useSpeechRecognition({
    lang: 'zh-CN',
    onResult: (text) => {
      // 防止多次 stop 清空，简单判空即可
      if (text) setInput(text);
    },
  });

  // 题目加载逻辑
  useEffect(() => {
    let isMounted = true;
    fetch(`https://www.cognospring.site/api/count`)
      .then(res => res.json())
      .then(({ total }) => { if (isMounted) setTotalQuestions(total); })
      .catch(console.error);

    const fetchQuestion = async (id) => {
      try {
        const response = await fetch(`https://www.cognospring.site/api/questions/${id}`);
        if (!response.ok) throw new Error('题目拉取失败');
        const data = await response.json();
        if (isMounted) {
          // 根据内容设置尝试次数
          const content = data.title || '';
          if (content.includes('入门')) {
            setRemainingAttempts(30);
          } else if (content.includes('简单')) {
            setRemainingAttempts(10);
          } else if (content.includes('中等')) {
            setRemainingAttempts(20);
          } else if (content.includes('困难')) {
            setRemainingAttempts(30);
          } else {
            data.title += '(难度未知)'
            setRemainingAttempts(30); // 默认值
          }
          setQuestion(data);
          setInput('');
          setMessages([]);
          setGameOver(false);
          setHintIndex(0);
          setCurrentHint('');
          setHintHistory([]);
          setLikes(data.likes);
          setHasLiked(false); // 每道题重置点赞状态
        }
      } catch (error) {
        console.error('Error fetching question:', error);
      }
    };
    fetchQuestion(questionId);

    return () => { isMounted = false; };
  }, [questionId]);

  // 获取提示
  const fetchHint = async () => {
    let totalHints = 0
    const content = question.title || '';
    if (content.includes('入门')) {
      totalHints = 10
    } else if (content.includes('简单')) {
      totalHints = 3
    } else if (content.includes('中等')) {
      totalHints = 5
    } else if (content.includes('困难')) {
      totalHints = 10
    } else {
      totalHints = 10
    }
    
    if (hintIndex >= totalHints) {
      setMessages(prev => [...prev, {
        text: `提示次数已经用完`,
        isUser: true 
      }]);
      return;
    }
    
    try {
      setMessages(prev => [...prev, { text: `获取提示`, isUser: true }]);
      const response = await fetch(`https://www.cognospring.site/api/keywords/${questionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          history: messages.map(m => m.text),
          correct_answer: question.answer,
          hint_index:hintIndex,
        })
      });
      
      if (!response.ok) throw new Error();
      const data = await response.json();
      
      setMessages(prev => [...prev, { 
        text: `提示${hintIndex + 1}/${totalHints}: ${data.keyword}`,
        isUser: false 
      }]);
      setHintIndex(prev => prev + 1);
      setRemainingAttempts(prev => Math.max(0, prev - 1));
    } catch {
      setCurrentHint('提示获取失败，请稍后重试');
    }
  };

  // 提交提问
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    setLoading(true);
    const newMessages = [...messages, { text: input, isUser: true }];
    setMessages(newMessages);
    try {
      const response = await fetch(`https://www.cognospring.site/api/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_id: questionId,
          user_answer: input,
          history: newMessages.map(m => m.text),
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail?.[0]?.msg || errorData.error || '请求失败');
      }
      const result = await response.json();
      if (result.error) {
        setMessages([
          ...newMessages,
          { text: `⚠️ 错误：${result.error}`, isUser: false }
        ]);
        return;
      }
      setMessages([
        ...newMessages,
        {
          text: result.llm_response,  // 直接使用后端返回的LLM生成文本
          isUser: false,
          suggestion: result.suggestion,
        }
      ]);
    } catch {
      setMessages([
        ...newMessages,
        {
          text: '⚠️ 请求失败，请重试',
          isUser: false,
        },
      ]);
    } finally {
      setLoading(false);
      setRemainingAttempts(prev => Math.max(0, prev - 1));
    }
    setInput('');
  };

  // 最终答案相似度
  const handleCheckSimilarity = async () => {
    if (!input.trim()) return;
    setSimilarityLoading(true);
    setLoading(true);
    setMessages(prev => [...prev, {
      text: `提交答案: ${input.trim()}`,
      isUser: true 
    }]);
    try {
      const response = await fetch(`https://www.cognospring.site/api/check_similarity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_id: questionId,
          user_answer: input
        })
      });
      if (!response.ok) throw new Error();
      const result = await response.json();
      setMessages(prev => [...prev, {
        text: `${result.similarity}\n\n正确答案：${result.correct_answer}`,
        isUser: false
      }]);
    } catch {
      setMessages(prev => [...prev, {
        text: '⚠️ 相似度检查失败',
        isUser: false
      }]);
    } finally {
      setSimilarityLoading(false);
      setLoading(false);
    }
  };

  // 点赞
  const handleLike = async () => {
    if (hasLiked) return;
    try {
      const response = await fetch(`https://www.cognospring.site/api/questions/${questionId}/like`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (!response.ok) {
        if (response.status === 429) {
          setNotification('⚠️ 每天只能点赞一次');
          setTimeout(() => setNotification(null), 1000); // 5秒后自动清除
          return;
        }
        throw new Error('请求失败');
      }
      const data = await response.json();
      setLikes(data.new_likes);
      setHasLiked(true);
    } catch {
      setNotification('⚠️ 点赞失败，请稍后重试');
      setTimeout(() => setNotification(null), 1000); // 5秒后自动清除
    }
  };

  const handleFeedback = async (messageIndex, feedbackType) => {
    try {
      setFeedbackStatus(prev => ({ ...prev, [messageIndex]: feedbackType }));

      const response = await fetch(`https://www.cognospring.site/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message_id: messages[messageIndex].id,
          feedback: feedbackType,
          all_messages: messages,
          question_id: questionId,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error('反馈提交失败');
      }
      setNotification(feedbackType === 'good' ? '感谢您的正面反馈！' : '我们会改进回答质量！');
      setTimeout(() => setNotification(null), 1000); // 5秒后自动清除
    } catch (error) {
      setNotification('反馈提交失败，请重试');
      setTimeout(() => setNotification(null), 1000); // 错误通知也自动清除
    }
  };

  // 上一/下一题
  useEffect(() => {
    // 新增获取排序问题列表
    fetch(`https://www.cognospring.site/api/list/questions`)
      .then(res => res.json())
      .then(data => setSortedQuestions(data))
      .catch(console.error);
  }, []);

  // 修改后的导航函数
  const getAdjacentId = (offset) => {
    const currentIndex = sortedQuestions.findIndex(q => q.id === questionId);
    const newIndex = Math.max(0, Math.min(sortedQuestions.length - 1, currentIndex + offset));
    return sortedQuestions[newIndex]?.id || questionId;
  };

  const nextQuestion = () => {
    navigate(`/game/${getAdjacentId(1)}`);
  };

  const previousQuestion = () => {
    navigate(`/game/${getAdjacentId(-1)}`);
  };

  if (!question) return <div>Loading...</div>;

  return (
    <div>
      <nav></nav>
      <h1>海龟汤问题精选</h1>
      {/* 玩法说明折叠控件 */}
      <div className="instructions-container">
        <button
          className="toggle-instructions"
          onClick={() => setShowInstructions(!showInstructions)}
        >
          {showInstructions ? '▲ 收起玩法说明' : '▼ 查看游戏规则'}
        </button>
        {showInstructions && (
          <div className="instructions-content">
            <p>🏮 欢迎来到海龟汤推理游戏！</p>
            <p>🔍 玩法说明：</p>
            <ul>
              <li>点击提交问题，提出对汤面的疑问</li>
              <li>AI裁判只会回答「是」「否」「是也不是」「不重要」四种答案</li>
              <li>如果没有思路，不妨看看提示</li>
              <li>认为接近真相时可以点击提交最终提交你认为的结局</li>
              <li>AI会自动评分并给出最终答案</li>
            </ul>
          </div>
        )}
      </div>
      <div class="button-container">
        <button onClick={() => navigate('/')} className="home-button">首页</button>
        <button onClick={previousQuestion} className="previous-question-btn" disabled={sortedQuestions[0]?.id === questionId}>上一题</button>
        <button onClick={nextQuestion} className="next-question-btn" disabled={sortedQuestions[sortedQuestions.length - 1]?.id === questionId}>下一题</button>
      </div>
      {/* 当前题目公开区 */}
      <div className="question-container">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', position: 'relative' }}>
          <h2 style={{ margin: 0 }}>{question?.title || ''}</h2>
          <button
            onClick={handleLike}
            disabled={hasLiked}
            className="like-button"
          >
            {hasLiked ? '❤️' : '🤍'} {likes || 0}
          </button>
          {notification && (
            <div className="notification" style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: '#fffbe6',
              border: '1px solid #faad14',
              color: '#985f0d',
              borderRadius: 4,
              padding: '12px 24px',
              zIndex: 1000,
              fontSize: 16,
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              textAlign: 'center',
              minWidth: '200px',
              animation: 'none' // 禁用动画效果
            }}>
              {notification}
            </div>
          )}
        </div>
        <p style={{ whiteSpace: 'pre-line' }}>{question?.content || ''}</p>
        <div className="button-container" style={{
          display: 'flex',
          gap: '10px',
          marginTop: '15px',
          flexWrap: 'wrap',
          width: '100%',
          maxWidth: '600px'
        }}>
        </div>
      </div>
      <div className="attempts-counter">
        🎮 剩余尝试次数: {remainingAttempts}
      </div>
      {/* 消息/回复展示区域 */}
      <div className="chat-container">
        <div className="messages-content">
          {messages.map((message, index) => {
            const lastBotIndex = messages.length - 1 - [...messages].reverse().findIndex(m => !m.isUser);

            return (
              <div
                key={index}
                className={`message ${message.isUser ? 'user' : 'bot'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  const lastBotIndex = messages.length - 1 - [...messages].reverse().findIndex(m => !m.isUser);
                  const isLast = index === lastBotIndex;

                  setSelectedBubble(prev => prev === index ? -1 : index);
                }}
              >
                <div className="bubble">
                  {message.text}
                  {message.suggestion && (
                    <div className="suggestion">{message.suggestion}</div>
                  )}
                </div>
                {/* 修改反馈按钮显示条件 */}
                {!message.isUser && (index === selectedBubble) && (
                  <div className="feedback-buttons">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFeedback(index, 'good')
                      }}
                      className={feedbackStatus[index] === 'good' ? 'active' : ''}
                    >
                      牛啊
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFeedback(index, 'bad')
                      }}
                      className={feedbackStatus[index] === 'bad' ? 'active' : ''}
                    >
                      离谱
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
        {/* 提交问题表单（文本输入/语音） */}
        <form onSubmit={handleSubmit}>
          <div className="input-button-group">
            <div className="answer-input">
              <div className="input-wrapper">
                <textarea
                  id="answer"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  required
                  disabled={gameOver || loading || remainingAttempts === 0}
                />
              </div>
            </div>
            <div className="inline-buttons">
              <button type="submit" className="button-group-btn" disabled={loading || remainingAttempts === 0 || similarityLoading}>
                {remainingAttempts === 0 ? '次数已用尽' : (loading ? 'AI分析中...' : '提交问题')}
              </button>
              <button
                type="button"
                className="button-group-btn"
                onClick={fetchHint}
                disabled={loading || gameOver || hintIndex >= Math.max(4 - question.difficulty, 1) || similarityLoading}
              >
                提示
              </button>
              <button
                type="button"
                className="button-group-btn"
                onClick={e => { e.preventDefault(); handleCheckSimilarity(); }}
                disabled={loading || gameOver || remainingAttempts === 0 || similarityLoading}
              >
                {similarityLoading ? 'AI分析中...' : '提交最终答案'}
              </button>
              <button
                type="button"
                className={`button-group-btn ${isListening ? 'listening' : ''}`}
                onClick={isListening ? stopListening : startListening}
              >
                {isListening ? '🛑 停止识别' : '🎙️ 开始识别'}
              </button>
            </div>
          </div>
        </form>
        {currentHint && <div className="hint-text">{currentHint}</div>}
        {/* 在组件外层添加点击关闭功能 */}
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'none' }}
          onClick={() => {
            setSelectedBubble(-1);
          }}
        />
      </div>
    </div>
  );
}

// 顶层App组件，负责前端路由
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/game/:id" element={<GamePage />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/admin2" element={<AdminPage />} />
      </Routes>
    </Router>
  );
}

export default App;
